import { getSmallDetailsValidatorColor } from '@/components/SmallDetails';
import { ProductPrintPositionAddModel } from '@/typings';
import { AnnotationShape } from '@/web-components/import-wizard-v2/typings/common';
import { NotificationResult } from '@/web-components/import-wizard-v2/typings/notification';
import { ProductPrintData } from '@midocean/webapps-shared-domain';
import { useStore as useProductStore } from '@/stores/v2/product';

export function getCommercialRemarkNotifications(
  unavailablePrintPositions: ProductPrintPositionAddModel[],
  productPrintData: ProductPrintData
): NotificationResult[] {
  const remarkNotifications: NotificationResult[] = [];

  for (const position of unavailablePrintPositions) {
    const printPostion = productPrintData.productPrintPositions?.find(
      (p) => p.id === position.positionId
    );

    const printTechnique = printPostion?.printTechniques.find((technique) => {
      return technique.id === position.technique.id;
    });

    if (printPostion && printTechnique) {
      const hasCommercialRemarks =
        (printTechnique.commercialRemarksCodes &&
          printTechnique.commercialRemarksCodes.length > 0) ||
        (printTechnique.printTechnique.group.commercialRemarksCodes &&
          printTechnique.printTechnique.group.commercialRemarksCodes.length >
            0);

      if (hasCommercialRemarks) {
        const groupRemarksCodes =
          printTechnique.printTechnique.group.commercialRemarksCodes || [];
        const techniqueRemarksCodes =
          printTechnique.commercialRemarksCodes || [];

        const uniqueCommertialCodes = [
          ...new Set(groupRemarksCodes.concat(techniqueRemarksCodes)),
        ];

        for (const code of uniqueCommertialCodes) {
          if (code) {
            remarkNotifications.push({
              validatorColor: 'Orange',
              validatorName: 'commercialRemark',
              printPositionName: printPostion.name,
              message: code.trim(),
            });
          }
        }
      }
    }
  }

  return remarkNotifications;
}

export function getEmptyPositionNotifications(
  unavailablePrintPositions: ProductPrintPositionAddModel[],
  positionShapeMap: Record<string, string>[]
): NotificationResult[] {
  const emptyPositionNotifications: NotificationResult[] = [];

  for (const position of unavailablePrintPositions) {
    const hasShapes = positionShapeMap.some(
      (x) => x.id === position.positionId
    );
    if (!hasShapes) {
      emptyPositionNotifications.push({
        validatorColor: 'Red',
        validatorName: 'emptyPrintPosition',
        message: '',
        printPositionName: position.positionName,
      });
    }
  }

  return emptyPositionNotifications;
}

export function getSmallDetailsNotifications(
  shapes: AnnotationShape[],
  positionShapeMap: Record<string, string>[],
  unavailablePrintPositions: ProductPrintPositionAddModel[]
): NotificationResult[] {
  const smallDetailsNotifications: NotificationResult[] = [];

  for (const shape of shapes) {
    if (
      shape.smallDetailsResult &&
      shape.smallDetailsResult.status !== 'NoCheck'
    ) {
      const positionId = positionShapeMap.find(
        (x) => x.logoId === shape.id
      )?.id;
      const position = unavailablePrintPositions.find(
        (x) => x.positionId === positionId
      );
      if (position) {
        smallDetailsNotifications.push({
          validatorColor: getSmallDetailsValidatorColor(
            shape.smallDetailsResult.status
          ),
          validatorName: 'smallDetails',
          shapeId: shape.id,
          printPositionName: position.positionName,
          message: '',
        });
      }
    }
  }
  return smallDetailsNotifications;
}

export function getOutOfBoundariesNotifications(
  shapes: AnnotationShape[],
  positionShapeMap: Record<string, string>[],
  positions: ProductPrintPositionAddModel[]
): NotificationResult[] {
  const outOfBoundariesNotifications: NotificationResult[] = [];

  for (const shape of shapes) {
    if (!shape.isInArea) {
      const positionId = positionShapeMap.find(
        (psm) => psm.logoId === shape.id
      )?.id;
      const position = positions.find((x) => x.positionId === positionId);
      if (position) {
        outOfBoundariesNotifications.push({
          validatorName: 'outOfBoundaries',
          validatorColor: 'Red',
          shapeId: shape.id,
          printPositionName: position.positionName,
          message: '',
        });
      }
    }
  }
  return outOfBoundariesNotifications;
}

export function getGradientFileNotifications(
  shapes: AnnotationShape[],
  positionShapeMap: Record<string, string>[],
  positions: ProductPrintPositionAddModel[]
): NotificationResult[] {
  const gradientFileNotifications: NotificationResult[] = [];
  const productStore = useProductStore();

  for (const shape of shapes) {
    if (shape.kind === 'Logo') {
      const positionId = positionShapeMap.find(
        (psm) => psm.logoId === shape.id
      )?.id;
      const position = positions.find((x) => x.positionId === positionId);
      const isTraditionalTechnique = productStore.isTraditionalTechnique(
        position?.positionId,
        position?.technique.printTechniqueSAPCode
      );

      if (position && shape.hasGradient && isTraditionalTechnique) {
        gradientFileNotifications.push({
          validatorName: 'gradientFileValidator',
          validatorColor: 'Red',
          shapeId: shape.id,
          printPositionName: position.positionName,
          message: '',
        });
      }
    }
  }
  return gradientFileNotifications;
}

export function getRecommendedPrintPositionNotifications(
  shapes: AnnotationShape[],
  positionShapeMap: Record<string, string>[],
  positions: ProductPrintPositionAddModel[]
): NotificationResult[] {
  const productStore = useProductStore();
  const recommendedPrintPositionNotifications: NotificationResult[] = [];

  for (const shape of shapes) {
    const positionId = positionShapeMap.find(
      (psm) => psm.logoId === shape.id
    )?.id;
    const position = positions.find((x) => x.positionId === positionId);
    const hasAnchorPoint = productStore.hasAnchorPointOnPrintPosition(
      position?.positionId || ''
    );
    if (!shape.isOnRecommendedPrintPosition && hasAnchorPoint) {
      recommendedPrintPositionNotifications.push({
        validatorName: 'recommendedPrintPosition',
        validatorColor: 'Orange',
        shapeId: shape.id,
        printPositionName: position?.positionName || '',
        message: '',
      });
    }
  }
  return recommendedPrintPositionNotifications;
}
